import { AxiosStatic } from "axios";
import { toCamel, toSnake } from "snake-camel";

export interface EquipmentFitmentLine {
  itemId: number | null;
  name: string;
  qty: number;
  price: number;
  /** A percentage off the unit price. */
  discount?: number;
  lineTotal?: number;
}

export type EquipmentFitmentStageKey = "proforma" | "revised" | "invoice";

/** One column of the proforma · revised quote · invoice matrix. */
export interface EquipmentFitmentStage {
  stageDate: string | null;
  /**
   * Proforma and revised quote: what the items came to, VAT included, when
   * the stage was last saved. Invoice: the figure typed in from Hansa, or
   * null until somebody has.
   */
  total: number | null;
  reference: string | null;
  documentName: string | null;
  hasDocument: boolean;
  /** Whether a row exists yet; the server answers every stage either way. */
  exists?: boolean;
  canUpload?: boolean;
}

export interface EquipmentFitmentHistoryEntry {
  action: string;
  details: string | null;
  user: string | null;
  at: string | null;
}

export interface EquipmentFitment {
  id?: number;
  ticketNumber?: string;
  date?: string;
  createdAt?: string | null;
  quoteId?: number | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  closedBy?: string | null;
  closedAt?: string | null;
  /** companies.id is a uuid. */
  companyId: string | null;
  customerName?: string | null;
  hansaAcc: string | null;
  registrationNumber: string | null;
  vehicleTypeId: number | null;
  vehicleType?: string | null;
  vehicleCategoryId: number | null;
  vehicleCategory?: string | null;
  vehicleMake: string | null;
  checkedBy: string | null;
  agentName: string | null;
  scheduledDate: string | null;
  scheduledTime: string | null;
  /** Written on the job card: when the driver arrived and when they left. */
  arrivalTime?: string | null;
  departureTime?: string | null;
  notes: string | null;
  status?: string;
  /** Paid up front: no revised quote, straight from proforma to invoice. */
  cashSale?: boolean;
  /** The word to show: Pending, In Progress, Approved or Finalized. */
  displayStatus?: string;
  revision?: number;
  poNumber?: string | null;
  quoteTotal?: number;
  vatAmount?: number;
  totalPayable?: number;
  itemsLocked?: boolean;
  /** 0 until the proforma document is up, 1 until the revised quote's, 2 after. */
  phase?: number;
  canEditRevised?: boolean;
  canEditInvoice?: boolean;
  /** Approve is offered: the user may, and the revised quote is finished. */
  canApprove?: boolean;
  /** Finalize is offered: the user may, it is approved, and it is invoiced. */
  canFinalize?: boolean;
  /** The user may change items, prices and the revised quote at all. */
  canRevise?: boolean;
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  /** The user holds the permission, whatever state the ticket is in. */
  mayApprove?: boolean;
  mayFinalize?: boolean;
  /** The order number is in and the revised quote has gone out. */
  revisedReady?: boolean;
  /** The invoice number and value are in and the invoice has gone out. */
  invoiceReady?: boolean;
  lines: EquipmentFitmentLine[];
  stages?: Record<EquipmentFitmentStageKey, EquipmentFitmentStage | null>;
  history?: EquipmentFitmentHistoryEntry[];
}

export interface EquipmentFitmentFilters {
  tab?: "pending" | "approved" | "finalized";
  customer?: string | null;
  quoteId?: number | string | null;
  hansaAcc?: string | null;
  vehicleCategoryId?: number | null;
  date?: string | null;
}

/** What the ticket popup saves in one go. */
export interface EquipmentFitmentTicketInput {
  lines: EquipmentFitmentLine[];
  notes: string | null;
  /** The revised quote's reference. */
  poNumber: string | null;
  stages: {
    proforma: { stageDate: string | null };
    revised: { stageDate: string | null };
    invoice: {
      stageDate: string | null;
      reference: string | null;
      total: number | null;
    };
  };
}

/** An item as the fitment form sees it: a name and a price, nothing else. */
export interface EquipmentFitmentItem {
  id: number;
  name: string;
  price: number;
}

export default ($axios: AxiosStatic) => ({
  /** The lists the form is filled in from; no settings permission needed. */
  async vehicleTypes(): Promise<Array<{ id: number; name: string }>> {
    return (await $axios.get("/api/equipment-fitments/vehicle-types")).data
      .data;
  },

  async vehicleCategories(
    vehicleTypeId?: number | null
  ): Promise<
    Array<{ id: number; name: string; vehicleTypeId: number | null }>
  > {
    const response = (
      await $axios.get("/api/equipment-fitments/vehicle-categories", {
        params: toSnake({ vehicleTypeId: vehicleTypeId || undefined })
      })
    ).data.data;
    return response.map((row: any) => toCamel(row));
  },

  async vehicleMakes(
    vehicleTypeId?: number | null
  ): Promise<
    Array<{ id: number; name: string; vehicleTypeId: number | null }>
  > {
    const response = (
      await $axios.get("/api/equipment-fitments/vehicle-makes", {
        params: toSnake({ vehicleTypeId: vehicleTypeId || undefined })
      })
    ).data.data;
    return response.map((row: any) => toCamel(row));
  },

  /** The QA team, for the Checked by picker. */
  async qaUsers(): Promise<Array<{ id: number; name: string }>> {
    return (await $axios.get("/api/equipment-fitments/qa-users")).data.data;
  },

  async items(vehicleTypeId?: number | null): Promise<EquipmentFitmentItem[]> {
    return (
      await $axios.get("/api/equipment-fitments/items", {
        params: toSnake({ vehicleTypeId: vehicleTypeId || undefined })
      })
    ).data.data;
  },

  async list(
    filters: EquipmentFitmentFilters = {}
  ): Promise<EquipmentFitment[]> {
    const response = (
      await $axios.get("/api/equipment-fitments", {
        params: toSnake(filters)
      })
    ).data.data;
    return response.map((row: any) => toCamel(row) as EquipmentFitment);
  },

  async show(id: number): Promise<EquipmentFitment> {
    const response = (await $axios.get(`/api/equipment-fitments/${id}`)).data
      .data;
    return toCamel(response) as EquipmentFitment;
  },

  async create(data: EquipmentFitment): Promise<EquipmentFitment> {
    const response = (
      await $axios.post("/api/equipment-fitments", toSnake(data))
    ).data.data;
    return toCamel(response) as EquipmentFitment;
  },

  /** The general details. Send no `lines` and the items are left alone. */
  async update(
    id: number,
    data: Partial<EquipmentFitment>
  ): Promise<EquipmentFitment> {
    const response = (
      await $axios.put(`/api/equipment-fitments/${id}`, toSnake(data))
    ).data.data;
    return toCamel(response) as EquipmentFitment;
  },

  /** The items, notes, order number and stages, saved together. */
  async saveTicket(
    id: number,
    data: EquipmentFitmentTicketInput
  ): Promise<EquipmentFitment> {
    const response = (
      await $axios.put(`/api/equipment-fitments/${id}/ticket`, toSnake(data))
    ).data.data;
    return toCamel(response) as EquipmentFitment;
  },

  async approve(id: number): Promise<EquipmentFitment> {
    const response = (
      await $axios.post(`/api/equipment-fitments/${id}/approve`)
    ).data.data;
    return toCamel(response) as EquipmentFitment;
  },

  async close(id: number): Promise<EquipmentFitment> {
    const response = (await $axios.post(`/api/equipment-fitments/${id}/close`))
      .data.data;
    return toCamel(response) as EquipmentFitment;
  },

  async uploadStageDocument(
    id: number,
    stage: EquipmentFitmentStageKey,
    file: File
  ): Promise<EquipmentFitment> {
    const data = new FormData();
    data.append("document", file);
    const response = (
      await $axios.post(
        `/api/equipment-fitments/${id}/stages/${stage}/document`,
        data,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
    ).data.data;
    return toCamel(response) as EquipmentFitment;
  },

  async stageDocument(
    id: number,
    stage: EquipmentFitmentStageKey
  ): Promise<Blob> {
    return (
      await $axios.get(
        `/api/equipment-fitments/${id}/stages/${stage}/document`,
        { responseType: "blob" }
      )
    ).data;
  },

  /** The job card plus every uploaded document, in one zip. */
  async downloadZip(id: number): Promise<Blob> {
    return (
      await $axios.get(`/api/equipment-fitments/${id}/zip`, {
        responseType: "blob"
      })
    ).data;
  },

  /** The ticket on the delivery note / collection job card. */
  async jobCard(id: number): Promise<Blob> {
    return (
      await $axios.get(`/api/equipment-fitments/${id}/job-card`, {
        responseType: "blob"
      })
    ).data;
  },

  async remove(id: number): Promise<void> {
    await $axios.delete(`/api/equipment-fitments/${id}`);
  }
});
